import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@akuna/ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  className,
  disabled = false,
}: MultiSelectProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = React.useState(false);
  const resolvedPlaceholder = placeholder ?? t("selectPlaceholder");

  const handleUnselect = (value: string) => {
    onChange(selected.filter((s) => s !== value));
  };

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "min-h-[var(--target-mobile)] w-full justify-between gap-2 !rounded-md px-4 py-3 font-normal",
            className
          )}
        >
          {/* Sem altura fixa: a linha de chips cresce com o conteúdo e quebra
              linha (flex-wrap) em vez de vazar para fora da caixa. Acima de
              ~2 linhas (max-h-24) ela mesma rola, sem afetar a altura do
              grid de filtros ao redor. */}
          <div className="flex max-h-24 min-w-0 flex-1 flex-wrap items-center gap-1.5 overflow-y-auto overflow-x-hidden text-left">
            {selected.length > 0 ? (
              selected.map((value) => {
                const option = options.find((o) => o.value === value);
                return (
                  <Badge
                    variant="secondary"
                    key={value}
                    className="inline-flex shrink-0 items-center whitespace-nowrap"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnselect(value);
                    }}
                  >
                    {option?.label}
                    {/* Este chip já está dentro do <button> de trigger do
                        Popover (ver PopoverTrigger asChild acima). Um
                        <button> aninhado é HTML inválido — daí o role em
                        vez do elemento nativo, com o mesmo comportamento
                        de teclado (Enter/Space) e clique. */}
                    <span
                      aria-label={t("removeItem", { item: option?.label ?? value })}
                      className="ml-1 cursor-pointer ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleUnselect(value);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </span>
                  </Badge>
                );
              })
            ) : (
              <span className="block min-w-0 max-w-full truncate text-muted-foreground">{resolvedPlaceholder}</span>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 self-start opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[12rem] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("searchPlaceholder")} disabled={disabled} />
          <CommandList>
            <CommandEmpty>{t("noResults")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
